let cargando = null;

// Resuelve un objeto envoltorio: el Module de OpenCV es "thenable" y no debe pasarse directo a resolve().
export const loadCv = () => {
  if (cargando) return cargando;
  cargando = new Promise((resolve, reject) => {
    const listo = () => window.cv && window.cv.Mat;
    if (listo()) return resolve({ cv: window.cv });
    const s = document.createElement("script");
    s.src = "/vendor/opencv.js";
    s.async = true;
    s.onerror = () => { cargando = null; reject(new Error("No se pudo cargar el escáner")); };
    document.body.appendChild(s);
    const t0 = Date.now();
    const iv = setInterval(() => {
      if (listo()) { clearInterval(iv); resolve({ cv: window.cv }); }
      else if (Date.now() - t0 > 60000) { clearInterval(iv); cargando = null; reject(new Error("El escáner tardó demasiado en cargar")); }
    }, 200);
  });
  return cargando;
};

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

export const ordenar = (pts) => {
  const s = [...pts].sort((a, b) => a.x + a.y - (b.x + b.y));
  const d = [...pts].sort((a, b) => a.y - a.x - (b.y - b.x));
  return [s[0], d[0], s[3], d[3]];
};

function mejorCuadro(cv, bin, W, H) {
  const contours = new cv.MatVector();
  const hier = new cv.Mat();
  cv.findContours(bin, contours, hier, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
  let best = null;
  let bestArea = 0.08 * W * H;
  let cortada = false;
  const borde = (x, y) => x <= W * 0.01 || y <= H * 0.01 || x >= W * 0.99 || y >= H * 0.99;
  for (let i = 0; i < contours.size(); i++) {
    const c = contours.get(i);
    const area = cv.contourArea(c);
    if (area > bestArea && area < 0.95 * W * H) {
      const approx = new cv.Mat();
      cv.approxPolyDP(c, approx, 0.02 * cv.arcLength(c, true), true);
      const d = approx.data32S;
      const pts = Array.from({ length: approx.rows }, (_, k) => ({ x: d[k * 2], y: d[k * 2 + 1] }));
      const enBorde = pts.filter((p) => borde(p.x, p.y)).length;
      if (approx.rows === 4 && cv.isContourConvex(approx) && enBorde < 3) {
        best = pts;
        bestArea = area;
      } else if (enBorde >= 2 && area > 0.15 * W * H) {
        cortada = true;
      }
      approx.delete();
    }
    c.delete();
  }
  contours.delete(); hier.delete();
  if (best) return { quad: ordenar(best), area: bestArea };
  return cortada ? { cortada: true } : null;
}

export function detectar(cv, canvas) {
  const W = canvas.width, H = canvas.height;
  const src = cv.imread(canvas);
  const gray = new cv.Mat();
  cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
  const brillo = cv.mean(gray)[0];
  const blur = new cv.Mat();
  cv.GaussianBlur(gray, blur, new cv.Size(5, 5), 0);
  const edges = new cv.Mat();
  cv.Canny(blur, edges, 40, 120);
  const k = cv.Mat.ones(3, 3, cv.CV_8U);
  cv.dilate(edges, edges, k);
  let r = mejorCuadro(cv, edges, W, H);
  if (!r?.quad) {
    const th = new cv.Mat();
    cv.threshold(blur, th, 0, 255, cv.THRESH_BINARY + cv.THRESH_OTSU);
    const otsu = mejorCuadro(cv, th, W, H);
    if (otsu?.quad) r = otsu;
    th.delete();
  }
  [src, gray, blur, edges, k].forEach((m) => m.delete());
  return { ...(r || {}), brillo, W, H };
}

export function evaluar({ quad, area, brillo, cortada, W, H }) {
  if (brillo < 60) return "Falta luz";
  if (!quad) return cortada ? "Se corta la hoja, aléjate un poco" : "Apunta a la remisión";
  const m = 0.015;
  if (quad.some((p) => p.x < W * m || p.y < H * m || p.x > W * (1 - m) || p.y > H * (1 - m))) return "Se corta la hoja, aléjate un poco";
  if (area / (W * H) < 0.3) return "Acércate";
  const cx = quad.reduce((a, p) => a + p.x, 0) / 4, cy = quad.reduce((a, p) => a + p.y, 0) / 4;
  if (Math.abs(cx - W / 2) > W * 0.12 || Math.abs(cy - H / 2) > H * 0.12) return "Centra el documento";
  const [tl, tr, br, bl] = quad;
  const r1 = Math.min(dist(tl, tr), dist(bl, br)) / Math.max(dist(tl, tr), dist(bl, br));
  const r2 = Math.min(dist(tl, bl), dist(tr, br)) / Math.max(dist(tl, bl), dist(tr, br));
  if (r1 < 0.82 || r2 < 0.82) return "Endereza el celular";
  return null;
}

export const movimiento = (a, b, W) => (a && b ? a.reduce((s, p, i) => s + dist(p, b[i]), 0) / 4 / W : 1);

// Endereza la hoja, aplica filtro de escáner y mide nitidez. Devuelve {canvas, nitidez}.
export function escanear(cv, srcCanvas, quad) {
  const [tl, tr, br, bl] = quad;
  let w = Math.max(dist(tl, tr), dist(bl, br));
  let h = Math.max(dist(tl, bl), dist(tr, br));
  const esc = Math.min(1, 1700 / Math.max(w, h));
  w = Math.round(w * esc); h = Math.round(h * esc);
  const src = cv.imread(srcCanvas);
  const from = cv.matFromArray(4, 1, cv.CV_32FC2, [tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y]);
  const to = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, w, 0, w, h, 0, h]);
  const M = cv.getPerspectiveTransform(from, to);
  const warped = new cv.Mat();
  cv.warpPerspective(src, warped, M, new cv.Size(w, h), cv.INTER_LINEAR, cv.BORDER_REPLICATE);
  const gray = new cv.Mat();
  cv.cvtColor(warped, gray, cv.COLOR_RGBA2GRAY);
  const lap = new cv.Mat(), mean = new cv.Mat(), std = new cv.Mat();
  cv.Laplacian(gray, lap, cv.CV_64F);
  cv.meanStdDev(lap, mean, std);
  const nitidez = std.data64F[0] ** 2;
  const bg = new cv.Mat();
  const kk = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(25, 25));
  cv.dilate(gray, bg, kk);
  cv.medianBlur(bg, bg, 21);
  const out = new cv.Mat();
  cv.divide(gray, bg, out, 255);
  cv.normalize(out, out, 0, 255, cv.NORM_MINMAX);
  out.convertTo(out, -1, 1.25, -40);
  const cx = Math.round(w * 0.012), cy = Math.round(h * 0.012);
  const recorte = out.roi(new cv.Rect(cx, cy, w - 2 * cx, h - 2 * cy));
  const canvas = document.createElement("canvas");
  cv.imshow(canvas, recorte);
  [src, from, to, M, warped, gray, lap, mean, std, bg, kk, out, recorte].forEach((m) => m.delete());
  return { canvas, nitidez };
}

export const NITIDEZ_MIN = 25;
